import React, { useState, useEffect, useRef } from 'react'

import { useFieldArray, useFormContext } from 'react-hook-form'
import autoAnimate from '@formkit/auto-animate'

import styles from './Forms.module.css'

import { useUser } from 'Services/User'
import { cloudinaryWidget } from 'Utils/cloudinary'

import { TextField } from 'components/Fields'
import Button from 'components/Button'
import Link from 'components/Link'
import Heading from 'components/Heading'

import ChevronUp from 'public/icons/chevron-up.svg'
import ChevronDown from 'public/icons/chevron-down.svg'
import Close from 'public/icons/close.svg'

const user_folder = process.env.NEXT_PUBLIC_CLOUDINARY_FOLDER_USER

const Achievements = ({ name, validations, heading, options }) => {
  const [adding, setAdding] = useState(false)
  const [current, setCurrent] = useState()
  const [fieldError, setError] = useState()
  const parent = useRef(null)
  const { profile } = useUser()

  useEffect(() => {
    parent.current && autoAnimate(parent.current)
  }, [parent])

  const {
    register,
    watch,
    control,
    formState: { errors },
  } = useFormContext()

  const error = errors[name]
  const values = watch(name)

  const { fields, append, update, remove, move } = useFieldArray({
    control,
    name: name,
  })

  const cloudinary_options = {
    clientAllowedFormats: ['jpg', 'jpeg', 'webp', 'png', 'pdf', 'avif', 'heif'],
    singleUploadAutoClose: true,
    folder: `${user_folder}/${profile?.unique_id}/achievements`,
    tags: ['achievement'],
    uploadPreset: 'achievements',
    minImageHeight: 800,
    minImageWidth: 800,
    maxImageHeight: 2500,
    maxImageWidth: 2500,
    maxImageFileSize: 50000000,
  }

  const handleAdd = () => {
    append({ name: '', link_value: '' })
    setAdding(true)
  }

  const addFile = (selected_index) => {
    const current_item = values[selected_index]

    const handleUpload = (error, result) => {
      if (error) {
        setError(name, error.statusText)
      }

      if (result.event === 'success') {
        const response = result.info
        const uploaded_file = response.secure_url
        // console.log({ current_item, uploaded_file })

        update(selected_index, { ...current_item, link_value: uploaded_file })
      }

      setAdding(false)
    }

    cloudinaryWidget(cloudinary_options, handleUpload)
  }

  return (
    <div className={styles['step-wrapper']}>
      <div className={styles['achievements-wrapper']}>
        <div className={styles['form-heading-actions']}>
          {heading && (
            <Heading element="h4" bold nomargin>
              {heading}
            </Heading>
          )}
          <Button buttonElement primary className={styles['add-achievement']} onClick={() => handleAdd()}>
            Add Achievement
          </Button>
        </div>
        <div ref={parent}>
          {fields?.length > 0 &&
            fields.map((field, index) => {
              return (
                <div className={styles['achievement-item']} key={field.id}>
                  <div className={styles['achievement-name-wrapper']}>
                    <div className={styles['achievement-name']}>
                      <TextField nomargin name={`${name}.${index}.name`} label={`Achievement Name`} />
                    </div>
                    <div className={styles['achievement-file']}>
                      {field && field.link_value ? (
                        <Link highlightText underline blank href={field.link_value}>
                          View
                        </Link>
                      ) : (
                        <Button buttonElement primary block onClick={() => addFile(index)}>
                          Add File
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className={styles['achievement-actions']}>
                    <div className={styles['achievement-move-up']}>
                      <div className={styles['achievement-move-button']} onClick={() => move(index, index - 1)}>
                        <ChevronUp />
                      </div>
                    </div>
                    <div className={styles['achievement-move-down']}>
                      <div className={styles['achievement-move-button']} onClick={() => move(index, index + 1)}>
                        <ChevronDown />
                      </div>
                    </div>
                    <div className={styles['achievement-remove']}>
                      <div onClick={() => remove(index)}>
                        <Close />
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
        </div>
      </div>

      {error?.type == 'required' && <div className={styles['error']}>{`${label} is required`}</div>}
      {error?.message && <div className={styles['error']} dangerouslySetInnerHTML={{ __html: error.message }}></div>}

      {fieldError && <div className={styles['error']}>{fieldError}</div>}
    </div>
  )
}

export default Achievements
